/**
 * Укладка запретных районов на карту MapLibre. Отдельным модулем, как районы и
 * рубежи ИЭЗ: карт в кабинете не одна, а вторая копия укладки разъехалась бы
 * с первой при первой же правке.
 *
 * Слои — РУБЕЖИ (префикс `fb-` в `BOUNDARY_PREFIXES`): лежат поверх любой
 * картинки и ложатся под линии районов, если те уже на карте, — номер района
 * читается сквозь бледную заливку запрета.
 */
import type maplibregl from 'maplibre-gl'

import { BAN_DASH, BAN_IN_FORCE, BAN_LABEL_MIN_ZOOM, banColor, banLabels, banLayerId } from './bans'
import { OVERLAY_PREFIX } from './layerOrder'

export const BAN_SOURCE_ID = OVERLAY_PREFIX + 'fb-src'
export const BAN_LABEL_SOURCE_ID = OVERLAY_PREFIX + 'fb-label-src'

/** Снизу вверх: заливка действующих, пунктир прочих, кромка действующих, номер. */
export const BAN_LAYER_IDS: string[] = [
  banLayerId('fill'),
  banLayerId('idle'),
  banLayerId('line'),
  banLayerId('label'),
]

export function applyBanLayers(
  map: maplibregl.Map,
  data: GeoJSON.FeatureCollection,
  beforeId?: string,
): void {
  const labels = banLabels(data)
  const existing = map.getSource(BAN_SOURCE_ID) as maplibregl.GeoJSONSource | undefined
  if (existing) {
    existing.setData(data)
    ;(map.getSource(BAN_LABEL_SOURCE_ID) as maplibregl.GeoJSONSource | undefined)?.setData(labels)
    return
  }
  map.addSource(BAN_SOURCE_ID, { type: 'geojson', data })
  map.addSource(BAN_LABEL_SOURCE_ID, { type: 'geojson', data: labels })

  const before = beforeId && map.getLayer(beforeId) ? beforeId : undefined
  const color = banColor()
  const inForce = BAN_IN_FORCE as unknown as maplibregl.FilterSpecification
  const idle = ['!', BAN_IN_FORCE] as unknown as maplibregl.FilterSpecification

  map.addLayer({
    id: banLayerId('fill'),
    type: 'fill',
    source: BAN_SOURCE_ID,
    filter: inForce,
    // Бледно: заливка поверх поля прогноза, и густая спрятала бы ровно то,
    // ради чего карту открывают.
    paint: { 'fill-color': color, 'fill-opacity': 0.16 },
  }, before)
  map.addLayer({
    id: banLayerId('idle'),
    type: 'line',
    source: BAN_SOURCE_ID,
    filter: idle,
    paint: { 'line-color': color, 'line-width': 1, 'line-opacity': 0.55,
             'line-dasharray': BAN_DASH },
  }, before)
  map.addLayer({
    id: banLayerId('line'),
    type: 'line',
    source: BAN_SOURCE_ID,
    filter: inForce,
    paint: { 'line-color': color, 'line-width': 1.6 },
  }, before)
  map.addLayer({
    id: banLayerId('label'),
    type: 'symbol',
    source: BAN_LABEL_SOURCE_ID,
    minzoom: BAN_LABEL_MIN_ZOOM,
    filter: inForce,
    layout: {
      'text-field': ['get', '_label'] as unknown as string,
      'text-size': 11,
      'text-font': ['Noto Sans Bold', 'Open Sans Bold'],
    },
    paint: { 'text-color': color, 'text-halo-color': '#ffffff', 'text-halo-width': 1.4 },
  }, before)
}

export function removeBanLayers(map: maplibregl.Map): void {
  for (const id of [...BAN_LAYER_IDS].reverse()) {
    if (map.getLayer(id)) map.removeLayer(id)
  }
  for (const id of [BAN_LABEL_SOURCE_ID, BAN_SOURCE_ID]) {
    if (map.getSource(id)) map.removeSource(id)
  }
}
